/**
 * CompanyManager 객체의 기본 형태와 타입 정의
 * 담당자 정보를 관리하는 공통 모델
 */

// 타입 정의
export type CompanyManagerRole = 'owner' | 'operator' | 'general';
export type CompanyManagerStatus = 'active' | 'inactive' | 'pending';
export type CompanyManagerPermission = 'read' | 'write' | 'admin';

/**
 * 담당자 기본 정보 인터페이스
 */
export interface ICompanyManager {
  id: number | null;
  name: string;
  email: string;
  phone: string;
  position: string;
  department: string;
  companySeq: number | null;
  companyName: string;
  role: CompanyManagerRole;
  status: CompanyManagerStatus;
  permissions: CompanyManagerPermission[];
  createdAt: string | null;
  updatedAt: string | null;
}

/**
 * 담당자 생성 시 필요한 정보 (ID 제외)
 */
export type CompanyManagerCreate = Omit<ICompanyManager, 'id' | 'createdAt' | 'updatedAt'>;

/**
 * 담당자 업데이트 시 필요한 정보 (일부 필드만)
 */
export type CompanyManagerUpdate = Partial<Pick<ICompanyManager, 'name' | 'email' | 'phone' | 'position' | 'department' | 'role' | 'status' | 'permissions'>>;

/**
 * 담당자 기본 정보 타입 정의
 */
export const CompanyManagerTypes = {
  // 담당자 역할 타입
  ROLE: {
    OWNER: 'owner' as CompanyManagerRole,
    OPERATOR: 'operator' as CompanyManagerRole,
    GENERAL: 'general' as CompanyManagerRole
  },
  
  // 담당자 상태 타입
  STATUS: {
    ACTIVE: 'active' as CompanyManagerStatus,
    INACTIVE: 'inactive' as CompanyManagerStatus,
    PENDING: 'pending' as CompanyManagerStatus
  },
  
  // 담당자 권한 타입
  PERMISSION: {
    READ: 'read' as CompanyManagerPermission,
    WRITE: 'write' as CompanyManagerPermission,
    ADMIN: 'admin' as CompanyManagerPermission
  }
} as const;

/**
 * 담당자 기본 객체 형태
 */
export const CompanyManagerDefaults = {
  // 기본 담당자 정보
  basic: {
    id: null,
    name: "",
    email: "",
    phone: "",
    position: "",
    department: "",
    companySeq: null,
    companyName: "",
    role: CompanyManagerTypes.ROLE.GENERAL,
    status: CompanyManagerTypes.STATUS.ACTIVE,
    permissions: [CompanyManagerTypes.PERMISSION.READ],
    createdAt: null,
    updatedAt: null
  } as ICompanyManager,
  
  // 소유사 담당자 정보
  owner: {
    id: null,
    name: "",
    email: "",
    phone: "",
    position: "소유사 담당자",
    department: "운영팀",
    companySeq: null,
    companyName: "",
    role: CompanyManagerTypes.ROLE.OWNER,
    status: CompanyManagerTypes.STATUS.ACTIVE,
    permissions: [CompanyManagerTypes.PERMISSION.READ, CompanyManagerTypes.PERMISSION.WRITE],
    createdAt: null,
    updatedAt: null
  } as ICompanyManager,
  
  // 운영사 담당자 정보
  operator: {
    id: null,
    name: "",
    email: "",
    phone: "",
    position: "운영사 담당자",
    department: "운영팀",
    companySeq: null,
    companyName: "",
    role: CompanyManagerTypes.ROLE.OPERATOR,
    status: CompanyManagerTypes.STATUS.ACTIVE,
    permissions: [CompanyManagerTypes.PERMISSION.READ, CompanyManagerTypes.PERMISSION.WRITE],
    createdAt: null,
    updatedAt: null
  } as ICompanyManager
};

/**
 * 담당자 객체 생성 함수들
 */
export const CompanyManagerFactory = {
  /**
   * 기본 담당자 객체 생성
   * @param overrides - 덮어쓸 속성들
   * @returns 기본 담당자 객체
   */
  createBasic(overrides: Partial<ICompanyManager> = {}): ICompanyManager {
    return {
      ...CompanyManagerDefaults.basic,
      ...overrides
    };
  },
  
  /**
   * 소유사 담당자 객체 생성
   * @param overrides - 덮어쓸 속성들
   * @returns 소유사 담당자 객체
   */
  createOwner(overrides: Partial<ICompanyManager> = {}): ICompanyManager {
    return {
      ...CompanyManagerDefaults.owner,
      ...overrides
    };
  },
  
  /**
   * 운영사 담당자 객체 생성
   * @param overrides - 덮어쓸 속성들
   * @returns 운영사 담당자 객체
   */
  createOperator(overrides: Partial<ICompanyManager> = {}): ICompanyManager {
    return {
      ...CompanyManagerDefaults.operator,
      ...overrides
    };
  },
  
  /**
   * 특정 역할의 담당자 객체 생성
   * @param role - 담당자 역할
   * @param overrides - 덮어쓸 속성들
   * @returns 담당자 객체
   */
  createByRole(role: CompanyManagerRole, overrides: Partial<ICompanyManager> = {}): ICompanyManager {
    switch (role) {
      case CompanyManagerTypes.ROLE.OWNER:
        return this.createOwner(overrides);
      case CompanyManagerTypes.ROLE.OPERATOR:
        return this.createOperator(overrides);
      default:
        return this.createBasic(overrides);
    }
  }
};

/**
 * 담당자 유효성 검사 함수들
 */
export const CompanyManagerValidators = {
  /**
   * 담당자 객체 유효성 검사
   * @param manager - 검사할 담당자 객체
   * @returns 유효성 여부
   */
  isValid(manager: ICompanyManager | null | undefined): manager is ICompanyManager {
    return Boolean(manager && 
           manager.id && 
           manager.name && 
           manager.companySeq);
  },
  
  /**
   * 필수 필드 검사
   * @param manager - 검사할 담당자 객체
   * @returns 누락된 필드 목록
   */
  getMissingFields(manager: ICompanyManager): (keyof ICompanyManager)[] {
    const requiredFields: (keyof ICompanyManager)[] = ['name', 'companySeq'];
    const missing: (keyof ICompanyManager)[] = [];
    
    requiredFields.forEach(field => {
      if (!manager[field]) {
        missing.push(field);
      }
    });
    
    return missing;
  },
  
  /**
   * 이메일 형식 검사
   * @param email - 검사할 이메일
   * @returns 유효성 여부
   */
  isValidEmail(email: string): boolean {
    if (!email) return true; // 이메일이 필수가 아닌 경우
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  },
  
  /**
   * 전화번호 형식 검사
   * @param phone - 검사할 전화번호
   * @returns 유효성 여부
   */
  isValidPhone(phone: string): boolean {
    if (!phone) return true; // 전화번호가 필수가 아닌 경우
    const phoneRegex = /^[0-9-+\s()]+$/;
    return phoneRegex.test(phone);
  }
};

/**
 * 담당자 데이터 변환 함수들
 */
export const CompanyManagerTransformers = {
  /**
   * API 응답을 담당자 객체로 변환
   * @param apiResponse - API 응답 데이터
   * @returns 변환된 담당자 객체
   */
  fromApiResponse(apiResponse: any): ICompanyManager {
    if (!apiResponse) return CompanyManagerFactory.createBasic();
    
    return {
      id: apiResponse.id || apiResponse.seq || null,
      name: apiResponse.name || "",
      email: apiResponse.email || "",
      phone: apiResponse.phone || apiResponse.phoneNumber || "",
      position: apiResponse.position || "",
      department: apiResponse.department || "",
      companySeq: apiResponse.companySeq || apiResponse.company?.seq || null,
      companyName: apiResponse.companyName || apiResponse.company?.name || "",
      role: apiResponse.role || CompanyManagerTypes.ROLE.GENERAL,
      status: apiResponse.status || CompanyManagerTypes.STATUS.ACTIVE,
      permissions: apiResponse.permissions || [CompanyManagerTypes.PERMISSION.READ],
      createdAt: apiResponse.createdAt || null,
      updatedAt: apiResponse.updatedAt || null
    };
  },
  
  /**
   * 담당자 객체를 API 요청 형태로 변환
   * @param manager - 담당자 객체
   * @returns API 요청 형태
   */
  toApiRequest(manager: ICompanyManager): CompanyManagerCreate {
    return {
      name: manager.name,
      email: manager.email,
      phone: manager.phone,
      position: manager.position,
      department: manager.department,
      companySeq: manager.companySeq,
      role: manager.role,
      status: manager.status,
      permissions: manager.permissions
    };
  },
  
  /**
   * 담당자 객체를 표시용으로 변환
   * @param manager - 담당자 객체
   * @returns 표시용 객체
   */
  toDisplayFormat(manager: ICompanyManager) {
    return {
      id: manager.id,
      displayName: `${manager.name} (${manager.position})`,
      company: manager.companyName,
      contact: manager.email || manager.phone || "연락처 없음",
      role: manager.role,
      status: manager.status
    };
  }
};

/**
 * 기본 export
 */
export default {
  Types: CompanyManagerTypes,
  Defaults: CompanyManagerDefaults,
  Factory: CompanyManagerFactory,
  Validators: CompanyManagerValidators,
  Transformers: CompanyManagerTransformers
};
